import React from 'react';
import { Badge } from '@/components/ui/badge';
import { FileText, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Type-safe status enum
type TranscriptionStatus = 'pending' | 'processing' | 'completed' | 'failed';

// Type-safe speaker enum
type Speaker = 'Agent' | 'Customer';

interface TranscriptionMessage {
	speaker: Speaker;
	text: string;
}

interface TranscriptionSectionProps {
	transcription?: string | null;
	transcriptionStatus?: TranscriptionStatus;
	isVoicemail?: boolean;
	agentName?: string;
	variant?: 'default' | 'sidebar';
}

export default function TranscriptionSection({
	transcription,
	transcriptionStatus = 'pending',
	isVoicemail = false,
	agentName,
	variant = 'default',
}: TranscriptionSectionProps) {
	const agentLabel = agentName?.trim() || 'Agent';
	const isSidebar = variant === 'sidebar';

	// Don't render if no transcription and status is not processing or failed
	if (
		!transcription &&
		transcriptionStatus !== 'processing' &&
		transcriptionStatus !== 'failed'
	) {
		return null;
	}

	// Show simple message when transcript failed
	if (transcriptionStatus === 'failed') {
		return (
			<div className="flex items-center gap-2 py-2 px-4">
				<FileText className="w-4 h-4 text-muted-foreground shrink-0" />
				<span className="text-sm text-muted-foreground">
					No transcript available
				</span>
			</div>
		);
	}

	// Parse transcription into structured messages
	const parseTranscription = (text: string): TranscriptionMessage[] => {
		if (!text) return [];

		const lines = text.split('\n').filter((line) => line.trim());
		const messages: TranscriptionMessage[] = [];

		lines.forEach((line) => {
			const agentMatch = line.match(/^Agent:\s*(.+)$/);
			const customerMatch = line.match(/^Customer:\s*(.+)$/);

			if (agentMatch) {
				messages.push({
					speaker: 'Agent',
					text: agentMatch[1].trim(),
				});
			} else if (customerMatch) {
				messages.push({
					speaker: 'Customer',
					text: customerMatch[1].trim(),
				});
			}
		});

		return messages;
	};

	const messages = transcription ? parseTranscription(transcription) : [];
	const showProcessing = transcriptionStatus === 'processing';

	return (
		<div className="w-full min-w-0 h-full flex flex-col bg-background">
			<div
				className={cn(
					'flex items-center gap-2 bg-background sticky top-0 z-10',
					isSidebar ? 'px-0 pb-2 pt-0' : 'px-4 pt-4 pb-2'
				)}
			>
				<FileText
					className={cn(
						'shrink-0',
						isSidebar ? 'w-4 h-4 text-foreground' : 'w-4 h-4 text-primary'
					)}
				/>
				<span
					className={cn(
						isSidebar
							? 'text-sm font-semibold text-foreground'
							: 'text-md font-medium text-primary'
					)}
				>
					Transcript
				</span>
				{showProcessing && (
					<Badge
						variant="secondary"
						className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800"
					>
						<Loader2 className="w-3 h-3 animate-spin" />
						Processing
					</Badge>
				)}
			</div>

			<div className="w-full min-w-0 flex-1 overflow-y-auto p-0">
				<div className="h-full">
					{showProcessing && !transcription ? (
						<div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
							<Loader2 className="w-4 h-4 animate-spin mr-2" />
							Transcription in progress...
						</div>
					) : messages.length > 0 ? (
						<div
							className={cn(
								'flex flex-col gap-3 w-full min-w-0',
								isSidebar ? 'p-0' : 'p-2'
							)}
						>
							{messages.map((message, index) => (
								<div key={index} className="w-full">
									<div
										className={cn(
											'px-3 py-2 w-full text-sm leading-relaxed rounded-xl',
											message.speaker === 'Agent'
												? 'bg-bubble text-foreground rounded-br-none'
												: 'bg-muted text-foreground rounded-bl-none'
										)}
									>
										{message.text}
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="text-sm text-muted-foreground text-center py-4">
							No transcription available
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
